import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { DECISIONS, ITEM_TONE, VERDICTS, errorMessage, orNull, suggestItems, type Verdict } from "@/features/think-tank/ttModel";

type TtReport = components["schemas"]["TtReportPublic"];
type TtRating = components["schemas"]["TtRatingPublic"];

const KEY = ["admin", "think-tank"] as const;

/** This week: the Monday report, its one recommendation, how sure, and the rating that teaches the next one. */
export function ThisWeek() {
  const qc = useQueryClient();
  const [week, setWeek] = useState<string | null>(null);
  const query = useQuery({
    queryKey: [...KEY, "report", week],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/report", { params: { query: { week_of: week } } });
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [...KEY] });
  const make = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/v1/admin/think-tank/report/generate");
      if (error) throw new Error(errorMessage(error, "Couldn’t make the report."));
    },
    onSuccess: refresh,
  });
  const v = query.data;
  if (query.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (query.isError || !v) {
    return (
      <Card><CardBody>
        <p role="alert" className="text-sm text-red-700">Couldn’t load this week’s report. Your session may have expired.</p>
        <Button size="sm" variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button>
      </CardBody></Card>
    );
  }
  const r = v.report;
  return (
    <div className="space-y-4">
      {!r ? (
        <Card data-testid="tt-report-empty"><CardBody>
          <p className="text-sm text-zinc-700">{v.empty_line}</p>
          <Button size="sm" className="mt-3" disabled={make.isPending} data-testid="tt-report-make" onClick={() => make.mutate()}>Make this week’s report now</Button>
          {make.isError ? <p role="alert" className="mt-2 text-sm text-red-700">{make.error.message}</p> : null}
        </CardBody></Card>
      ) : (
        <Report key={r.id} r={r} onChanged={refresh} />
      )}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {v.weeks.length > 1 ? (
          <>
            <label htmlFor="tt-week" className="text-zinc-600">Past reports</label>
            <select id="tt-week" value={r?.week_of ?? ""} onChange={e => setWeek(e.target.value || null)} data-testid="tt-week"
              className="h-8 rounded-md border border-zinc-300 px-2">
              {v.weeks.map(w => <option key={w.id} value={w.week_of}>{w.line}</option>)}
            </select>
          </>
        ) : null}
        {r && v.can_make ? <Button size="sm" variant="ghost" disabled={make.isPending} data-testid="tt-report-make" onClick={() => make.mutate()}>Make this week’s now</Button> : null}
      </div>
      <p className="text-xs text-zinc-500">{v.next_line}</p>
    </div>
  );
}

function Report({ r, onChanged }: { r: TtReport; onChanged: () => void }) {
  const decide = useMutation({
    mutationFn: async (decision: "pilot" | "deeper" | "not_now") => {
      const { error } = await api.POST("/api/v1/admin/think-tank/report/{report_id}/decide", {
        params: { path: { report_id: r.id } }, body: { decision },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t save that."));
    },
    onSuccess: onChanged,
  });
  return (
    <Card data-testid="tt-report"><CardBody>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs text-zinc-500" data-testid="tt-report-week">{r.week_line}</p>
        {r.sample ? <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-500">Sample</span> : null}
      </div>
      <h2 className="mt-1 text-lg font-semibold">{`${r.items.length} things worth your attention`}</h2>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        {r.items.map(i => (
          <div key={i.kind} className={`rounded-md border p-3 ${ITEM_TONE[i.kind] ?? "border-zinc-200"}`} data-testid={`tt-item-${i.kind}`}>
            <p className="text-xs font-semibold uppercase text-zinc-500">{i.label}</p>
            <p className="mt-1 font-medium">{i.title}</p>
            <p className="mt-1 text-sm text-zinc-700">{i.line}</p>
            <p className="mt-2 text-xs text-zinc-500">{i.evidence_line}</p>
          </div>
        ))}
      </div>
      {r.recommendation ? (
        <div className="mt-4 rounded-md border border-zinc-300 bg-zinc-50 p-3" data-testid="tt-recommendation">
          <p className="text-xs font-semibold uppercase text-zinc-500">Recommendation</p>
          <p className="mt-1 text-sm">{r.recommendation.line}</p>
          {r.decision_line ? (
            <p className="mt-2 text-sm font-medium" data-testid="tt-decision">{r.decision_line}</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {DECISIONS.map(([key, label]) => (
                <Button key={key} size="sm" variant={key === r.recommendation!.move ? "primary" : "secondary"} disabled={decide.isPending}
                  data-testid={`tt-decide-${key}`} onClick={() => decide.mutate(key)}>{label}</Button>
              ))}
            </div>
          )}
          {decide.isError ? <p role="alert" className="mt-1 text-sm text-red-700">{decide.error.message}</p> : null}
        </div>
      ) : null}
      {r.sure ? (
        <div className="mt-3 text-sm" data-testid="tt-sure">
          <span className="font-semibold">How sure is this? </span>{r.sure.line}
        </div>
      ) : null}
      {r.asks.length ? (
        <p className="mt-3 text-sm text-zinc-700" data-testid="tt-asks">{`You asked reports to cover: ${r.asks.join(" · ")}`}</p>
      ) : null}
      <p className="mt-2 text-xs text-zinc-500">{r.ai_line}</p>
      <Rate subject="report" id={r.id} ratings={r.ratings} onChanged={onChanged} />
    </CardBody></Card>
  );
}

/** Great · Missing something · Wrong, with a note whose points the rater turns into checklist items. */
export function Rate({ subject, id, ratings, onChanged }: { subject: "report" | "memo"; id: string; ratings: TtRating[]; onChanged: () => void }) {
  const mine = ratings.find(x => x.mine);
  const [verdict, setVerdict] = useState<Verdict | null>(mine?.verdict ?? null);
  const [note, setNote] = useState(mine?.note ?? "");
  const [items, setItems] = useState<string[]>([]);
  const [edited, setEdited] = useState(false);
  const shown = edited ? items : suggestItems(note);
  const save = useMutation({
    mutationFn: async () => {
      const body = { verdict: verdict!, note: orNull(note), checklist: shown.filter(x => x.trim()) };
      const { error } = subject === "report"
        ? await api.POST("/api/v1/admin/think-tank/report/{report_id}/rate", { params: { path: { report_id: id } }, body })
        : await api.POST("/api/v1/admin/think-tank/questions/{question_id}/rate", { params: { path: { question_id: id } }, body });
      if (error) throw new Error(errorMessage(error, "Couldn’t save the rating."));
    },
    onSuccess: onChanged,
  });
  return (
    <div className="mt-4 border-t border-zinc-100 pt-3" role="group" aria-label={`Rate this ${subject}`} data-testid={`tt-rate-${subject}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-zinc-600">{subject === "report" ? "Rate this report:" : "Rate this memo:"}</span>
        {VERDICTS.map(([key, label]) => (
          <Button key={key} size="sm" variant={verdict === key ? "primary" : "secondary"} data-testid={`tt-verdict-${subject}-${key}`}
            aria-pressed={verdict === key} onClick={() => setVerdict(key)}>{label}</Button>
        ))}
      </div>
      {ratings.filter(x => !x.mine || x.note).map(x => (
        <p key={x.by_name + x.created_at} className="mt-1 text-xs text-zinc-600">{`${x.by_name}: ${x.verdict_label}${x.note ? ` · “${x.note}”` : ""}`}</p>
      ))}
      {verdict ? (
        <div className="mt-2 space-y-2">
          <textarea aria-label="What was missing or wrong" placeholder="What was missing or wrong (optional)" value={note} data-testid={`tt-rate-note-${subject}`}
            onChange={e => setNote(e.target.value)} className="min-h-14 w-full rounded-md border border-zinc-300 p-2 text-sm" />
          {shown.length ? (
            <div className="space-y-1">
              <p className="text-xs text-zinc-600">→ The next run adds these to its checklist:</p>
              {shown.map((item, n) => (
                <input key={n} aria-label={`Checklist item ${n + 1}`} value={item} data-testid={`tt-rate-item-${subject}-${n}`}
                  onChange={e => { setEdited(true); setItems(shown.map((x, k) => k === n ? e.target.value : x)); }}
                  className="h-8 w-full rounded-md border border-zinc-300 px-2 text-sm" />
              ))}
            </div>
          ) : null}
          <Button size="sm" disabled={save.isPending} data-testid={`tt-rate-save-${subject}`} onClick={() => save.mutate()}>Save rating</Button>
          {save.isError ? <p role="alert" className="text-sm text-red-700">{save.error.message}</p> : null}
          {save.isSuccess ? <p className="text-sm text-zinc-700">Saved. The next run uses it.</p> : null}
        </div>
      ) : null}
    </div>
  );
}
